import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { Box, Stack, TableCell, Tooltip } from "@mui/material";
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import { StatusChip, PersonCell, SearchInput, FilterSelect } from "../../../../../../component/listing";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { getAllAssets } from "../../../../../../services/asset";
import { useOrgEmployees } from "../../../../../../services/useOrgData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, monoFamily, chartColors, motion } from "../../../../../../theme";
import { pressable } from "../../../../../../component/pressableProps";
import { counterRowSx } from "../../../../container/frameLayout";
import {
  buildRegister,
  summarise,
  stateCounts,
  typeBreakdown,
  fleetAge,
  matchesQuery,
  monthYear,
  shortDate,
  STATE_LABEL,
  STATE_TONE,
} from "../assetFacts";

/**
 * The asset overview.
 *
 * The book draws six counters, a value breakdown, an age chart, a state bar and
 * the register. Three of those lean on fields the Asset model does not have —
 * there is no purchase cost, no refresh-age policy and no repair state — so
 * this screen shows what the data supports and names what it cannot show,
 * rather than drawing a number nobody can trace back to a record. The
 * arithmetic is in ../assetFacts.js, where it is tested.
 */

// The state bar and the register badges take their colours from the product's
// status palette, not from the book's raw hexes — "not returned" has to be the
// same red here as everywhere else, or a badge stops meaning one thing.
const STATE_COLOR = {
  inUse: tokens.success,
  inStock: tokens.borderStrong,
  retired: tokens.pending,
  notReturned: tokens.danger,
};

const TONE_INK = {
  danger: tokens.danger,
  warning: tokens.warningText,
  success: tokens.successText,
};

/**
 * A counter.
 *
 * The label is the book's small-caps — 12px, 600, uppercase, 0.06em — which is
 * what keeps it distinct from the counter's own sub-line. `tone` colours the
 * number, and the book gives a colour only to the ones that cost money.
 */
const Counter = ({ label, value, sub, tone }) => (
  <Box
    sx={{
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1rem 1.125rem",
    }}
  >
    <Box
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
        mb: "0.625rem",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: displayFont,
        fontSize: "1.625rem",
        fontWeight: 700,
        lineHeight: 1.15,
        fontVariantNumeric: "tabular-nums",
        color: TONE_INK[tone] || tokens.text,
      }}
    >
      {value}
    </Box>
    {sub != null && (
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>{sub}</Box>
    )}
  </Box>
);

const Panel = ({ title, caption, right, children }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "18rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1.375rem",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: "1.25rem" }}>
      <Box>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
          {title}
        </Box>
        {caption && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.1875rem" }}>{caption}</Box>
        )}
      </Box>
      {right}
    </Stack>
    {children}
  </Box>
);

/** A section heading with a rule running out to whatever control sits beside it. */
const SectionHead = ({ title, right }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ md: "center" }}
    spacing={2}
    sx={{ mb: "1rem" }}
  >
    <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
      {title}
    </Box>
    <Box sx={{ flex: 1, height: "1px", background: tokens.border, display: { xs: "none", md: "block" } }} />
    {right}
  </Stack>
);

/**
 * Age of the fleet.
 *
 * The axis is the point the expenses pass got wrong: without a scale down the
 * left a column is a shape, and the reader has to hover every one to learn
 * anything. Four gridline labels and a baseline are enough to read a count off.
 *
 * No column is coloured as a problem. The book highlights "Over 4" as past
 * refresh age, but nothing on an asset says what its refresh age is, so an
 * amber column here would be an opinion dressed as a measurement.
 */
const AXIS_STEPS = 3;

const AgeChart = ({ age }) => {
  const peak = Math.max(1, age.max);
  // Round the top of the axis up to something quotable — "90 / 60 / 30 / 0"
  // rather than "88 / 58.7 / 29.3 / 0".
  const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
  const step = Math.max(1, Math.ceil(peak / AXIS_STEPS / (magnitude / 2 || 1)) * (magnitude / 2 || 1));
  const top = step * AXIS_STEPS;
  const ticks = Array.from({ length: AXIS_STEPS + 1 }, (_, i) => top - i * step);

  return (
    <Stack direction="row" spacing={1.5}>
      <Stack
        sx={{
          width: "1.875rem",
          flex: "none",
          height: "6.875rem",
          justifyContent: "space-between",
          alignItems: "flex-end",
          fontSize: "0.6875rem",
          color: tokens.textMuted,
          fontVariantNumeric: "tabular-nums",
          // The axis starts level with the top of the plot, which sits below the
          // row of counts.
          mt: "1.5rem",
        }}
      >
        {ticks.map((t) => (
          <Box key={t}>{Math.round(t)}</Box>
        ))}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* The counts sit in their own row above the bars: a figure pinned to a
            2%-tall column and one pinned to a full-height column land at
            different heights, and the row stops reading as a row. */}
        <Stack direction="row" spacing={1.5} sx={{ mb: "0.4375rem" }}>
          {age.buckets.map((b) => (
            <Box
              key={b.key}
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "0.6875rem",
                color: tokens.textMuted,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {b.count}
            </Box>
          ))}
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ height: "6.875rem", alignItems: "flex-end", borderBottom: `1px solid ${tokens.borderStrong}` }}
        >
          {age.buckets.map((b) => (
            <Tooltip key={b.key} title={`${b.label} years · ${b.count} of ${age.dated} dated`}>
              <Box
                sx={{
                  flex: 1,
                  height: `${Math.max(1, (b.count / top) * 100)}%`,
                  background: tokens.mutedDark,
                  borderRadius: "4px 4px 0 0",
                  transition: `height ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                }}
              />
            </Tooltip>
          ))}
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ mt: "0.5rem" }}>
          {age.buckets.map((b) => (
            <Box
              key={b.key}
              sx={{ flex: 1, textAlign: "center", fontSize: "0.6875rem", color: tokens.textMuted }}
            >
              {b.label}
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

/**
 * The one stacked bar in PayLoom.
 *
 * It earns the form here and nowhere else: every asset is in exactly one state,
 * so the widths sum to the fleet and the bar is a whole rather than a
 * comparison. Every other breakdown in the product is one row per category with
 * its own track.
 */
const StateBar = ({ counts }) => (
  <Box>
    <Box
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
        mb: "1rem",
      }}
    >
      Every asset is in one state
    </Box>
    <Stack direction="row" sx={{ height: "0.75rem", borderRadius: "6px", overflow: "hidden", mb: "1rem" }}>
      {counts.rows.map((r) => (
        <Tooltip key={r.key} title={`${r.label} · ${r.count}`}>
          <Box
            sx={{
              width: `${r.share * 100}%`,
              background: STATE_COLOR[r.key],
              transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
            }}
          />
        </Tooltip>
      ))}
    </Stack>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
        gap: "0.5625rem 1.125rem",
        fontSize: "0.8125rem",
      }}
    >
      {counts.rows.map((r) => (
        <Stack key={r.key} direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: "0.5625rem",
              height: "0.5625rem",
              borderRadius: "2px",
              background: STATE_COLOR[r.key],
              flex: "none",
            }}
          />
          <Box sx={{ flex: 1, color: tokens.textMuted }}>{r.label}</Box>
          <Box sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: tokens.text }}>{r.count}</Box>
        </Stack>
      ))}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, color: tokens.textMuted, fontWeight: 600 }}>Total</Box>
        <Box sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: tokens.text }}>{counts.total}</Box>
      </Stack>
    </Box>
  </Box>
);

/**
 * The register's pager.
 *
 * Three hundred assets is a scroll nobody finishes, and the book draws page
 * numbers with an ellipsis rather than an infinite list. It sits under the
 * table rather than inside it: the table's shell holds rows, and a control that
 * changes which rows those are is not one of them.
 */
const PAGE_SIZES = [20, 40, 100];

const pageList = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("…");
  for (let i = from; i <= to; i += 1) out.push(i);
  if (to < pages - 1) out.push("…");
  out.push(pages);
  return out;
};

const PageButton = ({ children, onClick, disabled, current }) => (
  <Box
    {...(onClick && !disabled ? pressable(onClick) : {})}
    sx={{
      minWidth: "2rem",
      height: "2rem",
      px: current === undefined ? "0.75rem" : 0,
      borderRadius: "8px",
      fontSize: "0.8125rem",
      fontWeight: current ? 600 : 400,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled || !onClick ? "default" : "pointer",
      color: disabled ? tokens.textInactive : current ? tokens.card : tokens.textMuted,
      background: current ? tokens.primary : "transparent",
      border: current === undefined ? `1px solid ${tokens.borderStrong}` : "none",
      transition: `background ${motion.duration[1]}ms ${motion.easing.standard}`,
      "&:hover": disabled || current || !onClick ? {} : { background: tokens.surfaceSubtle },
    }}
  >
    {children}
  </Box>
);

const Pager = ({ page, pages, size, onSize, onPage, from, to, total }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ md: "center" }}
    spacing={2}
    sx={{ mt: "1.125rem" }}
  >
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
      {total === 0 ? "No assets to show" : `Showing ${from}–${to} of ${total} asset${total === 1 ? "" : "s"}`}
    </Box>
    <Box sx={{ flex: 1 }} />
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Rows</Box>
      <FilterSelect
        value={size}
        onChange={(e) => onSize(Number(e.target.value))}
        options={PAGE_SIZES.map((n) => ({ value: n, label: String(n) }))}
        inputProps={{ "aria-label": "Rows per page" }}
      />
    </Stack>
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <PageButton disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</PageButton>
      {pageList(page, pages).map((p, i) =>
        p === "…" ? (
          <Box
            key={`gap-${i}`}
            sx={{ width: "2rem", textAlign: "center", fontSize: "0.8125rem", color: tokens.textInactive }}
          >
            …
          </Box>
        ) : (
          <PageButton key={p} current={p === page} onClick={() => onPage(p)}>{p}</PageButton>
        )
      )}
      <PageButton disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</PageButton>
    </Stack>
  </Stack>
);

// The breakdown's summary row sits on the page blush inside the table's own
// border, so its cells carry no hairline of their own.
const FOOT_SX = {
  borderBottom: "none",
  fontSize: "0.875rem",
  fontWeight: 700,
  color: tokens.text,
  py: "0.875rem",
};

const AssetOverview = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(PAGE_SIZES[1]);

  // The directory is where employmentStatus and lastWorkingDay live; the asset
  // endpoint populates only fname/lname/aliasId, so the leaver check needs both.
  const { data: employees } = useOrgEmployees();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await getAllAssets();
        if (res?.success === false) throw new Error(res?.message || "Failed to load assets");
        if (!active) return;
        setAssets(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (active) {
          setFailed(true);
          notify.error(err?.message || "Failed to load assets");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // One `today` per render pass, so the counters, the chart and the register
  // cannot disagree about which day it is halfway down the screen.
  const rows = useMemo(
    () => buildRegister(assets, employees || [], new Date()),
    [assets, employees]
  );

  const stats = useMemo(() => summarise(rows), [rows]);
  const states = useMemo(() => stateCounts(rows), [rows]);
  const breakdown = useMemo(() => typeBreakdown(rows), [rows]);
  const age = useMemo(() => fleetAge(rows), [rows]);

  const visible = useMemo(
    () => rows.filter((r) => (stateFilter === "all" || r.state === stateFilter) && matchesQuery(r, search)),
    [rows, search, stateFilter]
  );

  // A filter or a search that shortens the list must not leave the reader on a
  // page that no longer exists.
  useEffect(() => { setPage(1); }, [search, stateFilter, size]);

  const pages = Math.max(1, Math.ceil(visible.length / size));
  const current = Math.min(page, pages);
  const start = (current - 1) * size;
  const pageRows = visible.slice(start, start + size);

  const onPage = useCallback((p) => setPage(Math.max(1, p)), []);

  const machines = assets.filter((a) => a.category === "machine").length;
  const licences = assets.length - machines;

  const headline = loading
    ? " "
    : [
        `${stats.total} tracked`,
        `${stats.inUse} in use`,
        stats.notReturned
          ? `${stats.notReturned} still out with ${stats.leaverCount} ${stats.leaverCount === 1 ? "leaver" : "leavers"}`
          : "nothing out with a leaver",
      ].join(" · ");

  const peak = Math.max(1, ...breakdown.rows.map((r) => r.count));

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Assets"}
        breadcrumbs={[{ label: "Asset Tracker", href: "" }, { label: "Overview", href: "" }]}
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-start" }}
          spacing={1.5}
          sx={{ mb: "1.625rem" }}
        >
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>
              Assets
            </Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "0.25rem" }}>{headline}</Box>
          </Box>
          {/* The book's two actions are Import CSV and Assign asset. There is no
              import endpoint and no assign endpoint — assignment is a field on
              the asset — so these are the two routes that actually do it. */}
          <Stack direction="row" spacing={1} alignItems="center">
            <BtnSecondary to="/app/software" sx={{ borderRadius: "8px" }}>Software</BtnSecondary>
            <BtnPrimary to="/app/machines/form" sx={{ borderRadius: "8px" }}>Add machine</BtnPrimary>
          </Stack>
        </Stack>

        {/* Six counters. Two carry a colour rather than the book's three: the
            third one it colours is "in repair", and no field says so. */}
        <Box
          sx={{
            ...counterRowSx(6),
          }}
        >
          <Counter
            label="Tracked"
            value={stats.total}
            sub={`${machines} machine${machines === 1 ? "" : "s"} · ${licences} software`}
          />
          <Counter
            label="In use"
            value={stats.inUse}
            sub={`${Math.round(stats.inUseShare * 100)}% of the fleet assigned`}
          />
          <Counter label="In stock" value={stats.inStock} sub="ready to assign today" />
          <Counter
            label="Not returned"
            value={stats.notReturned}
            tone={stats.notReturned > 0 ? "danger" : undefined}
            sub={
              stats.notReturned
                ? `held by ${stats.leaverCount} ${stats.leaverCount === 1 ? "person who has left" : "people who have left"}`
                : "nobody has left holding one"
            }
          />
          <Counter label="Retired" value={stats.retired} sub="marked inactive" />
          <Counter
            label="Expiring"
            value={stats.expiring.soon}
            tone={stats.expiring.soon > 0 ? "warning" : undefined}
            sub={
              stats.expiring.lapsed
                ? `licences within 90 days · ${stats.expiring.lapsed} already lapsed`
                : "licences within 90 days"
            }
          />
        </Box>

        {/* What the book asks for and the data cannot answer. Named rather than
            drawn: a counter invented from nothing reads as a measurement. */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.75rem", lineHeight: 1.6 }}>
          Book value, refresh age and repair state are not tracked yet — an asset carries no purchase
          cost, no refresh policy, and a status of only active or inactive.
        </Box>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} sx={{ mt: "1.625rem", mb: "1.625rem" }}>
          <Box sx={{ flex: 1.15, minWidth: 0 }}>
            <SectionHead title="What the fleet is made of" />
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "-0.625rem", mb: "0.75rem" }}>
              By type, {breakdown.total} asset{breakdown.total === 1 ? "" : "s"}. Share is share of the
              count — there is no book value to divide.
            </Box>
            <DataTable
              rows={breakdown.rows}
              getRowId={(r) => r.name}
              loading={loading}
              error={failed}
              onRetry={() => setReloadKey((k) => k + 1)}
              errorTitle="Couldn't load assets"
              empty={{ title: "No assets yet", description: "Add a machine or a licence to start the register." }}
              tableSx={{ width: "100%", tableLayout: "fixed" }}
              columns={[
                {
                  key: "name",
                  label: "Category",
                  width: "30%",
                  render: (r) => (
                    <Stack direction="row" alignItems="center" spacing={1.125} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: "0.625rem",
                          height: "0.625rem",
                          borderRadius: "3px",
                          flex: "none",
                          background: chartColors[r.colorIndex % chartColors.length],
                        }}
                      />
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name}
                      </Box>
                    </Stack>
                  ),
                },
                {
                  key: "count",
                  label: "Qty",
                  width: "4.5rem",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums", color: tokens.textMuted },
                  render: (r) => r.count,
                },
                {
                  key: "bar",
                  label: "",
                  // Scaled against the largest row, not against the total: the
                  // share column already says how much of the fleet a row is,
                  // and the track is there to compare rows with each other.
                  render: (r) => (
                    <Box sx={{ height: "0.5rem", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: `${(r.count / peak) * 100}%`,
                          height: "100%",
                          background: chartColors[r.colorIndex % chartColors.length],
                          transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                        }}
                      />
                    </Box>
                  ),
                },
                {
                  key: "share",
                  label: "Share",
                  width: "5rem",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums", color: tokens.textMuted },
                  render: (r) => `${(r.share * 100).toFixed(1)}%`,
                },
              ]}
              footer={
                <>
                  <TableCell sx={FOOT_SX}>Total</TableCell>
                  <TableCell align="right" sx={{ ...FOOT_SX, fontVariantNumeric: "tabular-nums" }}>
                    {breakdown.total}
                  </TableCell>
                  <TableCell sx={FOOT_SX} />
                  <TableCell align="right" sx={{ ...FOOT_SX, fontVariantNumeric: "tabular-nums" }}>
                    {breakdown.total ? "100%" : "—"}
                  </TableCell>
                </>
              }
            />
          </Box>

          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={2.5}>
            <Panel
              title="Age of the fleet"
              caption="Years since purchase"
              right={
                age.undated > 0 ? (
                  <Box sx={{ textAlign: "right" }}>
                    <Box
                      sx={{
                        fontFamily: displayFont,
                        fontSize: "1.375rem",
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                        color: tokens.text,
                      }}
                    >
                      {age.undated}
                    </Box>
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>no purchase date</Box>
                  </Box>
                ) : null
              }
            >
              {age.dated === 0 ? (
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
                  No asset carries a purchase date, so there is no age to chart. Only software records a
                  purchase date today; machines have none to record.
                </Box>
              ) : (
                <AgeChart age={age} />
              )}
            </Panel>

            <Box
              sx={{
                background: tokens.card,
                border: `1px solid ${tokens.border}`,
                borderRadius: "10px",
                p: "1.375rem",
              }}
            >
              <StateBar counts={states} />
            </Box>
          </Stack>
        </Stack>

        <SectionHead
          title="The register"
          right={
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterSelect
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                options={[
                  { value: "all", label: "All states" },
                  ...states.rows.map((r) => ({ value: r.key, label: r.label })),
                ]}
                inputProps={{ "aria-label": "Asset state" }}
              />
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tag, asset or holder"
              />
            </Stack>
          }
        />

        <DataTable
          rows={pageRows}
          loading={loading}
          error={failed}
          onRetry={() => setReloadKey((k) => k + 1)}
          errorTitle="Couldn't load the register"
          empty={{
            title: rows.length === 0 ? "No assets yet" : "No matches",
            description:
              rows.length === 0
                ? "Add a machine or a licence to start the register."
                : "Try a different search or state.",
          }}
          onRowClick={(r) =>
            navigate(r.category === "software" ? "/app/software/form" : "/app/machines/form", {
              state: { assetId: r._id, mode: "view" },
            })
          }
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            {
              key: "tag",
              label: "Tag",
              width: "17%",
              // Tags are monospace, always: a serial is read character by
              // character off the back of a machine, and a proportional face
              // makes 0/O and 1/l ambiguous exactly when it matters.
              cellSx: { fontFamily: monoFamily, fontSize: "0.8125rem", color: tokens.textMuted },
              render: (r) => r.tag || "—",
            },
            {
              key: "name",
              label: "Asset",
              width: "26%",
              render: (r) => (
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontWeight: 500, color: tokens.text }}>
                    {r.detail ? `${r.name} · ${r.detail}` : r.name}
                  </Box>
                  {/* Why it is in its state is a line under the asset, never a
                      sixth badge. */}
                  {r.reason && (
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>{r.reason}</Box>
                  )}
                </Box>
              ),
            },
            {
              key: "holder",
              label: "Assigned to",
              width: "24%",
              render: (r) =>
                !r.holder ? (
                  <Box sx={{ color: tokens.textPlaceholder }}>Unassigned</Box>
                ) : r.holder.kind === "department" ? (
                  <Box sx={{ color: tokens.text }}>{r.holder.name}</Box>
                ) : (
                  <PersonCell
                    name={r.holder.name}
                    src={r.holder.profileImage}
                    // A leaver's unreturned asset shows the person *and* that
                    // they left — the name alone reads as somebody still here.
                    secondary={
                      r.holder.leaver ? (
                        <Box component="span" sx={{ color: tokens.danger }}>
                          {r.holder.lastWorkingDay ? `left ${shortDate(r.holder.lastWorkingDay)}` : "has left"}
                        </Box>
                      ) : null
                    }
                  />
                ),
            },
            {
              key: "inServiceOn",
              // Not "Since": there is no assignment date on an asset, and
              // labelling a service date as one would be a lie in a column.
              label: "In service",
              width: "13%",
              cellSx: { color: tokens.textMuted, whiteSpace: "nowrap" },
              render: (r) => monthYear(r.inServiceOn) || "—",
            },
            {
              key: "state",
              label: "Status",
              width: "9rem",
              render: (r) => <StatusChip label={STATE_LABEL[r.state]} tone={STATE_TONE[r.state]} />,
            },
          ]}
        />

        {!loading && !failed && rows.length > 0 && (
          <Pager
            page={current}
            pages={pages}
            size={size}
            onSize={setSize}
            onPage={onPage}
            from={start + 1}
            to={start + pageRows.length}
            total={visible.length}
          />
        )}
      </MainBox>
    </Box>
  );
};

export default AssetOverview;
